/**
 * Daily Mission Service - Misi Harian gamification
 * Tracks: Sholat 5 waktu (self-report), Dikir, Mengaji (5 ayat min)
 * Storage: localStorage (no auth required). Optional API sync later.
 * All dates use user's local timezone.
 */

import { getLocalDateString } from "../utils/dateUtils";

const PRAYER_STORAGE_KEY = "daily_missions_prayer";
const DIKIR_STORAGE_KEY = "daily_missions_dikir";
const DIKIR_TYPES_STORAGE_KEY = "daily_missions_dikir_types";
const NOTIFICATION_PREFS_KEY = "daily_missions_notification_prefs";

/** Dikir types required for Misi Harian: id 1=Subhanallah, 2=Alhamdulillah, 3=Allahu Akbar — masing-masing 33x */
export const DIKIR_TYPES_REQUIRED = [1, 2, 3];
export const DIKIR_TYPE_LABELS = { 1: "Subhanallah", 2: "Alhamdulillah", 3: "Allahu Akbar" };

/** Prayer type mapping: API keys (Fajr, Dhuhr...) to storage keys (fajr, dhuhr...) */
export const PRAYER_TYPES = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
export const PRAYER_LABELS = {
  fajr: "Subuh",
  dhuhr: "Dzuhur",
  asr: "Ashar",
  maghrib: "Maghrib",
  isha: "Isya",
};

/**
 * Remove per-person mission progress from this device. Called on logout / session
 * expiry so the next account on the same phone does not inherit it.
 * Notification prefs are a device setting and are kept.
 */
export function clearDailyMissionProgress() {
  try {
    [PRAYER_STORAGE_KEY, DIKIR_STORAGE_KEY, DIKIR_TYPES_STORAGE_KEY, "daily_missions_prayer_dismissed"].forEach((k) =>
      localStorage.removeItem(k)
    );
  } catch {}
}

/** @returns {string} YYYY-MM-DD for today (user's local timezone) */
function todayStr() {
  return getLocalDateString();
}

// --- Prayer ---

/** Load prayer records: { "2026-03-19": ["fajr","dhuhr",...] } */
function loadPrayerRecords() {
  try {
    const raw = localStorage.getItem(PRAYER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/** Save prayer records */
function savePrayerRecords(records) {
  try {
    localStorage.setItem(PRAYER_STORAGE_KEY, JSON.stringify(records));
  } catch {}
}

/**
 * Record that user completed a prayer today.
 * @param {string} prayerType - fajr, dhuhr, asr, maghrib, isha
 * @returns {{ recorded: boolean, completedToday: string[] }}
 */
export function recordPrayer(prayerType) {
  const today = todayStr();
  const normalized = (prayerType || "").toLowerCase();
  if (!PRAYER_TYPES.includes(normalized)) return { recorded: false, completedToday: [] };

  const records = loadPrayerRecords();
  const todayList = records[today] || [];
  if (todayList.includes(normalized)) {
    return { recorded: false, completedToday: todayList };
  }

  records[today] = [...todayList, normalized].sort();
  savePrayerRecords(records);
  return { recorded: true, completedToday: records[today] };
}

/**
 * Get prayers completed today.
 * @returns {string[]} e.g. ["fajr","dhuhr"]
 */
export function getPrayersCompletedToday() {
  const today = todayStr();
  const records = loadPrayerRecords();
  return records[today] || [];
}

/**
 * Check if a specific prayer is completed today.
 * @param {string} prayerType - fajr, dhuhr, asr, maghrib, isha
 */
export function isPrayerCompletedToday(prayerType) {
  return getPrayersCompletedToday().includes((prayerType || "").toLowerCase());
}

const PRAYER_DISMISSED_KEY = "daily_missions_prayer_dismissed";

function loadPrayerDismissed() {
  try {
    const raw = localStorage.getItem(PRAYER_DISMISSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function savePrayerDismissed(records) {
  try {
    localStorage.setItem(PRAYER_DISMISSED_KEY, JSON.stringify(records));
  } catch {}
}

/** Record that user dismissed (clicked Belum) for this prayer today. Won't show again for that prayer today. */
export function recordPrayerDismissed(prayerType) {
  const today = todayStr();
  const normalized = (prayerType || "").toLowerCase();
  if (!PRAYER_TYPES.includes(normalized)) return;
  const records = loadPrayerDismissed();
  const todayList = records[today] || [];
  if (todayList.includes(normalized)) return;
  records[today] = [...todayList, normalized].sort();
  savePrayerDismissed(records);
}

/** @returns {string[]} Prayer keys user dismissed today (clicked Belum) */
export function getPrayersDismissedToday() {
  const today = todayStr();
  const records = loadPrayerDismissed();
  return records[today] || [];
}

// --- Dikir ---

/** Load dikir types completed per day: { "2026-03-19": [1, 2, 3] } */
function loadDikirTypesRecords() {
  try {
    const raw = localStorage.getItem(DIKIR_TYPES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveDikirTypesRecords(records) {
  try {
    localStorage.setItem(DIKIR_TYPES_STORAGE_KEY, JSON.stringify(records));
  } catch {}
}

/**
 * Record that user completed 33x for a dikir type today.
 * Types: 1=Subhanallah, 2=Alhamdulillah, 3=Allahu Akbar
 * @param {number} zikirId - 1, 2, or 3
 * @returns {{ recorded: boolean, typesCompleted: number[], allComplete: boolean }}
 */
export function recordDikirTypeCompleted(zikirId) {
  const id = parseInt(zikirId, 10);
  if (!DIKIR_TYPES_REQUIRED.includes(id)) return { recorded: false, typesCompleted: [], allComplete: false };

  const today = todayStr();
  const records = loadDikirTypesRecords();
  const todayList = records[today] || [];
  if (todayList.includes(id)) {
    const completed = [...todayList].sort();
    return {
      recorded: false,
      typesCompleted: completed,
      allComplete: DIKIR_TYPES_REQUIRED.every((r) => completed.includes(r)),
    };
  }

  records[today] = [...todayList, id].sort();
  saveDikirTypesRecords(records);
  const completed = records[today];
  const allComplete = DIKIR_TYPES_REQUIRED.every((r) => completed.includes(r));
  return { recorded: true, typesCompleted: completed, allComplete };
}

/**
 * Get dikir types (1,2,3) completed today.
 * @returns {number[]} e.g. [1, 2] or [1, 2, 3]
 */
export function getDikirTypesCompletedToday() {
  const today = todayStr();
  const records = loadDikirTypesRecords();
  return records[today] || [];
}

/** Load dikir dates: ["2026-03-18","2026-03-19"] — dates when full mission (all 3 types) done */
function loadDikirDates() {
  try {
    const raw = localStorage.getItem(DIKIR_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveDikirDates(dates) {
  const unique = [...new Set(dates)].sort().reverse();
  try {
    localStorage.setItem(DIKIR_STORAGE_KEY, JSON.stringify(unique));
  } catch {}
  return unique;
}

function computeDikirStreaks(dates) {
  if (dates.length === 0) return { currentStreak: 0, longestStreak: 0 };
  const today = todayStr();
  const sorted = [...dates].sort();

  let currentStreak = 0;
  if (sorted.includes(today)) {
    currentStreak = 1;
    const todayIdx = sorted.indexOf(today);
    for (let i = todayIdx - 1; i >= 0; i--) {
      const prev = sorted[i];
      const expected = new Date(sorted[i + 1]);
      expected.setDate(expected.getDate() - 1);
      const expectedStr = `${expected.getFullYear()}-${String(expected.getMonth() + 1).padStart(2, "0")}-${String(expected.getDate()).padStart(2, "0")}`;
      if (prev === expectedStr) currentStreak++;
      else break;
    }
  }

  let longestStreak = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    prev.setDate(prev.getDate() + 1);
    const expectedStr = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}-${String(prev.getDate()).padStart(2, "0")}`;
    if (sorted[i] === expectedStr) {
      run++;
      longestStreak = Math.max(longestStreak, run);
    } else {
      run = 1;
    }
  }
  return { currentStreak, longestStreak };
}

/**
 * Record dikir completed today. Idempotent.
 * @returns {{ recorded: boolean, currentStreak: number, longestStreak: number }}
 */
export function recordDikirToday() {
  const today = todayStr();
  let dates = loadDikirDates();
  const alreadyRecorded = dates.includes(today);
  if (!alreadyRecorded) {
    dates = saveDikirDates([...dates, today]);
  }
  const { currentStreak, longestStreak } = computeDikirStreaks(dates);
  return {
    recorded: !alreadyRecorded,
    currentStreak,
    longestStreak,
    totalDays: dates.length,
  };
}

/**
 * Get dikir stats without recording.
 * todayRecorded = all 3 types (Subhanallah, Alhamdulillah, Allahu Akbar) completed today.
 * @returns {{ currentStreak: number, longestStreak: number, totalDays: number, todayRecorded: boolean, typesCompleted: number[] }}
 */
export function getDikirStats() {
  const dates = loadDikirDates();
  const { currentStreak, longestStreak } = computeDikirStreaks(dates);
  const today = todayStr();
  const typesCompleted = getDikirTypesCompletedToday();
  const todayRecorded = DIKIR_TYPES_REQUIRED.every((r) => typesCompleted.includes(r));
  return {
    currentStreak,
    longestStreak,
    totalDays: dates.length,
    todayRecorded,
    typesCompleted,
  };
}

// --- Notification prefs ---

const DEFAULT_NOTIFICATION_PREFS = {
  adhanReminder: true,
  solatReminder: true,
};

export function getNotificationPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_NOTIFICATION_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

export function setNotificationPrefs(prefs) {
  const current = getNotificationPrefs();
  const merged = { ...current, ...prefs };
  try {
    localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(merged));
  } catch {}
  return merged;
}

// --- Aggregate daily mission status ---

/**
 * Get full daily mission status for widget display.
 * Mengaji stats come from mengajiStreakService (imported at call site to avoid circular deps).
 * @param {object} mengajiStats - from getStreakStats() or getMengajiMissionStats()
 */
export function getDailyMissionStatus(mengajiStats = null) {
  const prayers = getPrayersCompletedToday();
  const dikir = getDikirStats();
  return {
    prayersCompleted: prayers,
    prayersCount: prayers.length,
    prayersTotal: 5,
    dikirCompleted: dikir.todayRecorded,
    dikirTypesCompleted: dikir.typesCompleted || [],
    dikirStreak: dikir.currentStreak,
    mengajiCompleted: mengajiStats ? mengajiStats.todayRecorded : false,
    mengajiVersesToday: mengajiStats?.versesReadToday ?? 0,
    mengajiStreak: mengajiStats?.liveStreak ?? mengajiStats?.currentStreak ?? 0,
  };
}
